import { useCallback } from "react";
import { useCspApi } from "@perfectserve/http-ps";
import { normalizeInteractionFinalAction, } from "../types/platformMetrics";
export const usePlatformMetrics = () => {
    const { get } = useCspApi();
    const fetchPlatformVolumeMetrics = useCallback(async (timePeriod = "30d", selectedInteractionTypes) => {
        try {
            const now = new Date();
            const startDate = new Date();
            const timePeriodDays = {
                "24h": 1,
                "7d": 7,
                "30d": 30,
                "90d": 90,
                "180d": 180,
                "1y": 365,
                "2y": 730,
            };
            const daysToSubtract = timePeriodDays[timePeriod];
            if (timePeriod === "1y" || timePeriod === "2y") {
                const yearsToSubtract = timePeriod === "1y" ? 1 : 2;
                startDate.setFullYear(now.getFullYear() - yearsToSubtract);
            }
            else {
                startDate.setDate(now.getDate() - daysToSubtract);
            }
            const startDateISO = startDate.toISOString();
            const endDateISO = now.toISOString();
            let dateFilter = `(InteractionStartDateUtc ge ${startDateISO} and InteractionStartDateUtc le ${endDateISO})`;
            if (selectedInteractionTypes && selectedInteractionTypes.length > 0) {
                const actionFilter = selectedInteractionTypes.length === 1
                    ? `InteractionFinalAction eq '${selectedInteractionTypes[0]}'`
                    : `(${selectedInteractionTypes.map((action) => `InteractionFinalAction eq '${action}'`).join(" or ")})`;
                dateFilter = `${dateFilter} and ${actionFilter}`;
            }
            const applyQuery = `filter(${dateFilter})/compute(` +
                `year(InteractionStartDateUtc) as Year,` +
                `month(InteractionStartDateUtc) as Month,` +
                `day(InteractionStartDateUtc) as Day,` +
                `InteractionFinalAction as FinalAction` +
                `)/groupby((Year,Month,Day,FinalAction),aggregate(` +
                `Id with countdistinct as TotalInteractions` +
                `))`;
            const encodedApply = encodeURIComponent(applyQuery);
            const url = `/api/reporting/interactions?$filter=(1 eq 1)&$apply=${encodedApply}&$orderby=Year,Month,Day,FinalAction asc`;
            const response = await get(url);
            const dailyVolumeData = response.data;
            const { weeklyMetrics, interactionTypes } = convertDailyVolumeToWeeklyMetrics(dailyVolumeData);
            const totalInteractions = dailyVolumeData.reduce((sum, item) => sum + (item.TotalInteractions || 0), 0);
            return {
                weeklyMetrics,
                interactionTypes,
                totalInteractions,
                dateRange: {
                    start: startDateISO,
                    end: endDateISO,
                },
            };
        }
        catch (error) {
            console.error("Error fetching platform volume metrics:", error);
            throw error;
        }
    }, [get]);
    return {
        fetchPlatformVolumeMetrics,
    };
};
const convertDailyVolumeToWeeklyMetrics = (dailyData) => {
    const weeklyMap = new Map();
    const allInteractionTypes = new Set();
    dailyData.forEach((day) => {
        const date = new Date(day.Year, day.Month - 1, day.Day);
        const finalAction = normalizeInteractionFinalAction(day.FinalAction);
        const count = day.TotalInteractions || 0;
        allInteractionTypes.add(finalAction);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const weekKey = weekStart.toISOString();
        if (!weeklyMap.has(weekKey)) {
            weeklyMap.set(weekKey, {
                weekStart,
                weekEnd,
                actions: new Map(),
            });
        }
        const weekData = weeklyMap.get(weekKey);
        const currentCount = weekData.actions.get(finalAction) || 0;
        weekData.actions.set(finalAction, currentCount + count);
    });
    const weeklyMetrics = [];
    const interactionTypes = Array.from(allInteractionTypes).sort();
    weeklyMap.forEach((week) => {
        const weekMetric = {
            weekStart: week.weekStart.toISOString(),
            weekEnd: week.weekEnd.toISOString(),
        };
        interactionTypes.forEach((actionType) => {
            weekMetric[actionType] = week.actions.get(actionType) || 0;
        });
        weeklyMetrics.push(weekMetric);
    });
    weeklyMetrics.sort((a, b) => new Date(a.weekStart).getTime() - new Date(b.weekStart).getTime());
    return {
        weeklyMetrics,
        interactionTypes,
    };
};
//# sourceMappingURL=usePlatformMetrics.js.map